/**
 * Lightweight ZIP3 (first three digits) → primary city lookup.
 *
 * Each ZIP3 corresponds to a USPS Sectional Center Facility city. This is
 * the "best we can do" without shipping a multi-MB ZIP5 dataset — the city
 * returned is an approximation (e.g. 018xx → Lawrence, MA even though 01844
 * is Methuen). For the profile header it produces a recognizable local area
 * name rather than a raw ZIP code or state abbreviation.
 *
 * Coverage: ~100 high-traffic ZIP3 codes across major US metros. Uncovered
 * ZIP3s fall back to state-only in zipToLocation().
 */
export const ZIP3_TO_CITY: Record<string, string> = {
  // Massachusetts / New England
  '010': 'Springfield, MA', '011': 'Springfield, MA', '012': 'Pittsfield, MA',
  '013': 'Greenfield, MA', '014': 'Worcester, MA', '015': 'Worcester, MA',
  '016': 'Worcester, MA', '017': 'Framingham, MA', '018': 'Lawrence, MA',
  '019': 'Haverhill, MA', '020': 'Brockton, MA', '021': 'Boston, MA',
  '022': 'Boston, MA', '023': 'Brockton, MA', '024': 'Brockton, MA',
  '025': 'Cape Cod, MA', '026': 'Cape Cod, MA', '027': 'Providence, RI',
  '028': 'Providence, RI', '029': 'Newport, RI',
  '030': 'Manchester, NH', '031': 'Manchester, NH', '032': 'White River Jct, VT',
  '033': 'Concord, NH', '034': 'Manchester, NH',
  '035': 'White River Jct, VT', '036': 'Brattleboro, VT', '037': 'Montpelier, VT',
  '038': 'Portsmouth, NH', '039': 'Portsmouth, NH',
  '040': 'Portland, ME', '041': 'Portland, ME', '042': 'Portland, ME',
  '043': 'Augusta, ME', '044': 'Bangor, ME', '045': 'Portland, ME',
  '046': 'Bangor, ME', '047': 'Augusta, ME', '048': 'Augusta, ME',
  '049': 'Houlton, ME',

  // Connecticut / NJ
  '060': 'Hartford, CT', '061': 'Hartford, CT', '062': 'Hartford, CT',
  '063': 'New Haven, CT', '064': 'New Haven, CT', '065': 'New Haven, CT',
  '066': 'Bridgeport, CT', '067': 'Waterbury, CT', '068': 'Stamford, CT',
  '069': 'Stamford, CT',
  '070': 'Newark, NJ', '071': 'Newark, NJ', '072': 'Elizabeth, NJ',
  '073': 'Jersey City, NJ', '074': 'Paterson, NJ', '075': 'Hackensack, NJ',
  '076': 'Hackensack, NJ', '077': 'Long Branch, NJ', '078': 'Dover, NJ',
  '079': 'Summit, NJ',
  '080': 'Camden, NJ', '081': 'Camden, NJ', '082': 'Atlantic City, NJ',
  '083': 'Atlantic City, NJ', '084': 'Trenton, NJ', '085': 'Trenton, NJ',
  '086': 'Trenton, NJ', '087': 'Lakewood, NJ', '088': 'New Brunswick, NJ',
  '089': 'New Brunswick, NJ',

  // New York
  '100': 'New York, NY', '101': 'New York, NY', '102': 'New York, NY',
  '103': 'Staten Island, NY', '104': 'Bronx, NY', '105': 'White Plains, NY',
  '106': 'White Plains, NY', '107': 'Yonkers, NY', '108': 'New Rochelle, NY',
  '109': 'White Plains, NY', '110': 'Queens, NY', '111': 'Queens, NY',
  '112': 'Brooklyn, NY', '113': 'Queens, NY', '114': 'Queens, NY',
  '115': 'Long Island, NY', '116': 'Far Rockaway, NY', '117': 'Long Island, NY',
  '118': 'Hicksville, NY', '119': 'Riverhead, NY',
  '120': 'Albany, NY', '121': 'Schenectady, NY', '122': 'Albany, NY',
  '123': 'Schenectady, NY', '124': 'Kingston, NY', '125': 'Poughkeepsie, NY',
  '126': 'Poughkeepsie, NY', '127': 'Monticello, NY', '128': 'Glens Falls, NY',
  '129': 'Plattsburgh, NY',
  '130': 'Syracuse, NY', '131': 'Syracuse, NY', '132': 'Syracuse, NY',
  '133': 'Utica, NY', '134': 'Utica, NY', '135': 'Watertown, NY',
  '136': 'Binghamton, NY', '137': 'Binghamton, NY', '138': 'Binghamton, NY',
  '139': 'Elmira, NY',
  '140': 'Buffalo, NY', '141': 'Buffalo, NY', '142': 'Buffalo, NY',
  '143': 'Niagara Falls, NY', '144': 'Rochester, NY', '145': 'Rochester, NY',
  '146': 'Rochester, NY', '147': 'Jamestown, NY', '148': 'Elmira, NY',
  '149': 'Ithaca, NY',

  // Pennsylvania
  '150': 'Pittsburgh, PA', '151': 'Pittsburgh, PA', '152': 'Pittsburgh, PA',
  '153': 'Washington, PA', '154': 'Pittsburgh, PA', '155': 'Johnstown, PA',
  '156': 'Greensburg, PA', '160': 'New Castle, PA', '161': 'Oil City, PA',
  '162': 'DuBois, PA', '163': 'Altoona, PA', '164': 'Erie, PA',
  '165': 'Erie, PA', '166': 'Altoona, PA', '167': 'Bradford, PA',
  '168': 'State College, PA', '169': 'Wellsboro, PA',
  '170': 'Harrisburg, PA', '171': 'Harrisburg, PA', '172': 'Harrisburg, PA',
  '173': 'Lancaster, PA', '174': 'Lancaster, PA', '175': 'Lancaster, PA',
  '176': 'Reading, PA', '177': 'Williamsport, PA', '178': 'Harrisburg, PA',
  '179': 'Pottsville, PA',
  '180': 'Lehigh Valley, PA', '181': 'Allentown, PA', '182': 'Hazleton, PA',
  '183': 'Stroudsburg, PA', '184': 'Scranton, PA', '185': 'Scranton, PA',
  '186': 'Wilkes-Barre, PA', '187': 'Wilkes-Barre, PA', '188': 'Montrose, PA',
  '189': 'Doylestown, PA', '190': 'Philadelphia, PA', '191': 'Philadelphia, PA',
  '192': 'Philadelphia, PA', '193': 'Paoli, PA', '194': 'Norristown, PA',
  '195': 'Reading, PA', '196': 'Reading, PA',

  // Delaware / DC / MD / VA
  '197': 'Wilmington, DE', '198': 'Wilmington, DE', '199': 'Wilmington, DE',
  '200': 'Washington, DC', '202': 'Washington, DC', '203': 'Washington, DC',
  '204': 'Washington, DC', '205': 'Washington, DC',
  '206': 'Silver Spring, MD', '207': 'Silver Spring, MD', '208': 'Bethesda, MD',
  '209': 'Silver Spring, MD', '210': 'Baltimore, MD', '211': 'Baltimore, MD',
  '212': 'Baltimore, MD', '214': 'Annapolis, MD', '215': 'Cumberland, MD',
  '216': 'Easton, MD', '217': 'Hagerstown, MD', '218': 'Salisbury, MD',
  '219': 'Baltimore, MD',
  '220': 'Fairfax, VA', '221': 'Fairfax, VA', '222': 'Arlington, VA',
  '223': 'Alexandria, VA', '224': 'Fredericksburg, VA', '225': 'Culpeper, VA',
  '226': 'Winchester, VA', '227': 'Culpeper, VA', '228': 'Harrisonburg, VA',
  '229': 'Charlottesville, VA', '230': 'Richmond, VA', '231': 'Richmond, VA',
  '232': 'Richmond, VA', '233': 'Norfolk, VA', '234': 'Norfolk, VA',
  '235': 'Newport News, VA', '236': 'Norfolk, VA', '237': 'Newport News, VA',
  '238': 'Petersburg, VA', '239': 'Farmville, VA', '240': 'Roanoke, VA',
  '241': 'Roanoke, VA', '242': 'Bristol, VA', '243': 'Pulaski, VA',
  '244': 'Staunton, VA', '245': 'Lynchburg, VA', '246': 'Lewisburg, WV',

  // West Virginia
  '247': 'Bluefield, WV', '248': 'Lewisburg, WV', '249': 'Beckley, WV',
  '250': 'Charleston, WV', '251': 'Charleston, WV', '252': 'Huntington, WV',
  '253': 'Charleston, WV', '254': 'Martinsburg, WV', '255': 'Huntington, WV',
  '256': 'Parkersburg, WV', '257': 'Parkersburg, WV', '258': 'Beckley, WV',
  '259': 'Lewisburg, WV', '260': 'Wheeling, WV', '261': 'Parkersburg, WV',
  '262': 'Clarksburg, WV', '263': 'Clarksburg, WV', '264': 'Morgantown, WV',
  '265': 'Fairmont, WV', '266': 'Morgantown, WV', '267': 'Petersburg, WV',
  '268': 'Petersburg, WV',

  // Carolinas
  '270': 'Greensboro, NC', '271': 'Winston-Salem, NC', '272': 'Greensboro, NC',
  '273': 'Winston-Salem, NC', '274': 'Greensboro, NC', '275': 'Raleigh, NC',
  '276': 'Durham, NC', '277': 'Raleigh, NC', '278': 'Rocky Mount, NC',
  '279': 'Elizabeth City, NC', '280': 'Charlotte, NC', '281': 'Charlotte, NC',
  '282': 'Charlotte, NC', '283': 'Fayetteville, NC', '284': 'Wilmington, NC',
  '285': 'Kinston, NC', '286': 'Hickory, NC', '287': 'Asheville, NC',
  '288': 'Asheville, NC', '289': 'Goldsboro, NC', '290': 'Columbia, SC',
  '291': 'Columbia, SC', '292': 'Columbia, SC', '293': 'Spartanburg, SC',
  '294': 'Charleston, SC', '295': 'Florence, SC', '296': 'Greenville, SC',
  '297': 'Rock Hill, SC', '298': 'Aiken, SC', '299': 'Beaufort, SC',

  // Georgia / Florida
  '300': 'Atlanta, GA', '301': 'Atlanta, GA', '302': 'Atlanta, GA',
  '303': 'Atlanta, GA', '304': 'Athens, GA', '305': 'Athens, GA',
  '306': 'Augusta, GA', '307': 'Gainesville, GA', '308': 'Augusta, GA',
  '309': 'Dublin, GA', '310': 'Savannah, GA', '311': 'Savannah, GA',
  '312': 'Waycross, GA', '313': 'Savannah, GA', '314': 'Albany, GA',
  '315': 'Columbus, GA', '316': 'Valdosta, GA', '317': 'Albany, GA',
  '318': 'Columbus, GA', '319': 'Macon, GA',
  '320': 'Jacksonville, FL', '321': 'Daytona Beach, FL', '322': 'Jacksonville, FL',
  '323': 'Tallahassee, FL', '324': 'Panama City, FL', '325': 'Pensacola, FL',
  '326': 'Gainesville, FL', '327': 'Orlando, FL', '328': 'Orlando, FL',
  '329': 'Melbourne, FL', '330': 'Miami, FL', '331': 'Miami, FL',
  '332': 'Miami, FL', '333': 'Fort Lauderdale, FL', '334': 'West Palm Beach, FL',
  '335': 'Tampa, FL', '336': 'Tampa, FL', '337': 'St Petersburg, FL',
  '338': 'Lakeland, FL', '339': 'Fort Myers, FL', '341': 'Naples, FL',
  '342': 'Sarasota, FL', '344': 'Gainesville, FL', '346': 'Spring Hill, FL',
  '347': 'Orlando, FL', '349': 'Stuart, FL',

  // Alabama / Tennessee
  '350': 'Birmingham, AL', '351': 'Birmingham, AL', '352': 'Birmingham, AL',
  '354': 'Tuscaloosa, AL', '355': 'Jasper, AL', '356': 'Decatur, AL',
  '357': 'Huntsville, AL', '358': 'Huntsville, AL', '359': 'Gadsden, AL',
  '360': 'Montgomery, AL', '361': 'Montgomery, AL', '362': 'Anniston, AL',
  '363': 'Dothan, AL', '364': 'Evergreen, AL', '365': 'Mobile, AL',
  '366': 'Mobile, AL', '367': 'Montgomery, AL', '368': 'Selma, AL',
  '369': 'Meridian, MS',
  '370': 'Nashville, TN', '371': 'Nashville, TN', '372': 'Nashville, TN',
  '373': 'Chattanooga, TN', '374': 'Chattanooga, TN', '375': 'Memphis, TN',
  '376': 'Johnson City, TN', '377': 'Knoxville, TN', '378': 'Knoxville, TN',
  '379': 'Knoxville, TN', '380': 'Memphis, TN', '381': 'Memphis, TN',
  '382': 'Jackson, TN', '383': 'Jackson, TN', '384': 'Columbia, TN',
  '385': 'Cookeville, TN',

  // Mississippi / Kentucky
  '386': 'Clarksdale, MS', '387': 'Greenville, MS', '388': 'Tupelo, MS',
  '389': 'Grenada, MS', '390': 'Jackson, MS', '391': 'Jackson, MS',
  '392': 'Jackson, MS', '393': 'Meridian, MS', '394': 'Hattiesburg, MS',
  '395': 'Gulfport, MS', '396': 'McComb, MS', '397': 'Columbus, MS',
  '400': 'Louisville, KY', '401': 'Louisville, KY', '402': 'Louisville, KY',
  '403': 'Lexington, KY', '404': 'Lexington, KY', '405': 'Lexington, KY',
  '406': 'Frankfort, KY', '407': 'London, KY', '408': 'London, KY',
  '409': 'London, KY', '410': 'Cincinnati, OH', '411': 'Ashland, KY',
  '412': 'Ashland, KY', '413': 'Campton, KY', '414': 'Campton, KY',
  '415': 'Pikeville, KY', '416': 'Pikeville, KY', '417': 'Hazard, KY',
  '418': 'Hazard, KY', '420': 'Paducah, KY', '421': 'Bowling Green, KY',
  '422': 'Bowling Green, KY', '423': 'Owensboro, KY', '424': 'Evansville, IN',
  '425': 'Somerset, KY', '426': 'Elizabethtown, KY', '427': 'Elizabethtown, KY',

  // Ohio
  '430': 'Columbus, OH', '431': 'Columbus, OH', '432': 'Columbus, OH',
  '433': 'Columbus, OH', '434': 'Toledo, OH', '435': 'Toledo, OH',
  '436': 'Toledo, OH', '437': 'Zanesville, OH', '438': 'Zanesville, OH',
  '439': 'Steubenville, OH', '440': 'Cleveland, OH', '441': 'Cleveland, OH',
  '442': 'Akron, OH', '443': 'Akron, OH', '444': 'Youngstown, OH',
  '445': 'Youngstown, OH', '446': 'Canton, OH', '447': 'Canton, OH',
  '448': 'Mansfield, OH', '449': 'Mansfield, OH', '450': 'Cincinnati, OH',
  '451': 'Cincinnati, OH', '452': 'Cincinnati, OH', '453': 'Dayton, OH',
  '454': 'Dayton, OH', '455': 'Springfield, OH', '456': 'Chillicothe, OH',
  '457': 'Athens, OH', '458': 'Lima, OH',

  // Indiana / Michigan
  '460': 'Indianapolis, IN', '461': 'Indianapolis, IN', '462': 'Indianapolis, IN',
  '463': 'Gary, IN', '464': 'Gary, IN', '465': 'South Bend, IN',
  '466': 'South Bend, IN', '467': 'Fort Wayne, IN', '468': 'Fort Wayne, IN',
  '469': 'Kokomo, IN', '470': 'Cincinnati, OH', '471': 'New Albany, IN',
  '472': 'Columbus, IN', '473': 'Muncie, IN', '474': 'Bloomington, IN',
  '475': 'Washington, IN', '476': 'Evansville, IN', '477': 'Evansville, IN',
  '478': 'Terre Haute, IN', '479': 'Lafayette, IN',
  '480': 'Royal Oak, MI', '481': 'Detroit, MI', '482': 'Detroit, MI',
  '483': 'Pontiac, MI', '484': 'Flint, MI', '485': 'Flint, MI',
  '486': 'Saginaw, MI', '487': 'Saginaw, MI', '488': 'Lansing, MI',
  '489': 'Lansing, MI', '490': 'Kalamazoo, MI', '491': 'Kalamazoo, MI',
  '492': 'Jackson, MI', '493': 'Grand Rapids, MI', '494': 'Grand Rapids, MI',
  '495': 'Grand Rapids, MI', '496': 'Traverse City, MI', '497': 'Gaylord, MI',
  '498': 'Iron Mountain, MI', '499': 'Iron Mountain, MI',

  // Midwest (WI, MN, IA, ND, SD, MT)
  '500': 'Des Moines, IA', '501': 'Des Moines, IA', '502': 'Des Moines, IA',
  '503': 'Des Moines, IA', '504': 'Mason City, IA', '505': 'Fort Dodge, IA',
  '506': 'Waterloo, IA', '507': 'Waterloo, IA', '508': 'Creston, IA',
  '510': 'Sioux City, IA', '511': 'Sioux City, IA', '512': 'Sioux City, IA',
  '513': 'Council Bluffs, IA', '514': 'Carroll, IA', '515': 'Omaha, NE',
  '516': 'Cherokee, IA', '520': 'Dubuque, IA', '521': 'Decorah, IA',
  '522': 'Cedar Rapids, IA', '523': 'Cedar Rapids, IA', '524': 'Ottumwa, IA',
  '525': 'Ottumwa, IA', '526': 'Burlington, IA', '527': 'Davenport, IA',
  '528': 'Davenport, IA',
  '530': 'Milwaukee, WI', '531': 'Milwaukee, WI', '532': 'Milwaukee, WI',
  '534': 'Racine, WI', '535': 'Beloit, WI', '537': 'Madison, WI',
  '538': 'Madison, WI', '539': 'Portage, WI', '540': 'St Paul, MN',
  '541': 'Green Bay, WI', '542': 'Green Bay, WI', '543': 'Green Bay, WI',
  '544': 'Wausau, WI', '545': 'Rhinelander, WI', '546': 'La Crosse, WI',
  '547': 'Eau Claire, WI', '548': 'Spooner, WI', '549': 'Oshkosh, WI',
  '550': 'St Paul, MN', '551': 'St Paul, MN', '553': 'Minneapolis, MN',
  '554': 'Minneapolis, MN', '555': 'Minneapolis, MN', '556': 'Duluth, MN',
  '557': 'Duluth, MN', '558': 'Duluth, MN', '559': 'Rochester, MN',
  '560': 'Mankato, MN', '561': 'Windom, MN', '562': 'Willmar, MN',
  '563': 'St Cloud, MN', '564': 'Brainerd, MN', '565': 'Detroit Lakes, MN',
  '566': 'Bemidji, MN', '567': 'Thief River Falls, MN',
  '570': 'Sioux Falls, SD', '571': 'Sioux Falls, SD', '572': 'Watertown, SD',
  '573': 'Mitchell, SD', '574': 'Aberdeen, SD', '575': 'Pierre, SD',
  '576': 'Mobridge, SD', '577': 'Rapid City, SD',
  '580': 'Fargo, ND', '581': 'Fargo, ND', '582': 'Grand Forks, ND',
  '583': 'Devils Lake, ND', '584': 'Jamestown, ND', '585': 'Bismarck, ND',
  '586': 'Dickinson, ND', '587': 'Minot, ND', '588': 'Williston, ND',
  '590': 'Billings, MT', '591': 'Billings, MT', '592': 'Wolf Point, MT',
  '593': 'Miles City, MT', '594': 'Great Falls, MT', '595': 'Havre, MT',
  '596': 'Helena, MT', '597': 'Butte, MT', '598': 'Missoula, MT',
  '599': 'Kalispell, MT',

  // Illinois
  '600': 'Chicago, IL', '601': 'Chicago, IL', '602': 'Evanston, IL',
  '603': 'Oak Park, IL', '604': 'Chicago, IL', '605': 'Aurora, IL',
  '606': 'Chicago, IL', '607': 'Elgin, IL', '608': 'Aurora, IL',
  '609': 'Kankakee, IL', '610': 'Rockford, IL', '611': 'Rockford, IL',
  '612': 'La Salle, IL', '613': 'Ottawa, IL', '614': 'Galesburg, IL',
  '615': 'Peoria, IL', '616': 'Peoria, IL', '617': 'Bloomington, IL',
  '618': 'Champaign, IL', '619': 'Champaign, IL', '620': 'East St Louis, IL',
  '622': 'East St Louis, IL', '623': 'Quincy, IL', '624': 'Effingham, IL',
  '625': 'Springfield, IL', '626': 'Springfield, IL', '627': 'Springfield, IL',
  '628': 'Centralia, IL', '629': 'Carbondale, IL',

  // Missouri / Kansas / Nebraska / Oklahoma / Texas
  '630': 'St Louis, MO', '631': 'St Louis, MO', '633': 'St Louis, MO',
  '634': 'Hannibal, MO', '635': 'Kirksville, MO', '636': 'Rolla, MO',
  '637': 'Cape Girardeau, MO', '638': 'Sikeston, MO', '639': 'Poplar Bluff, MO',
  '640': 'Kansas City, MO', '641': 'Kansas City, MO', '644': 'St Joseph, MO',
  '645': 'Maryville, MO', '646': 'Chillicothe, MO', '647': 'Harrisonville, MO',
  '648': 'Joplin, MO', '650': 'Springfield, MO', '651': 'Jefferson City, MO',
  '652': 'Columbia, MO', '653': 'Sedalia, MO', '654': 'Lebanon, MO',
  '655': 'Springfield, MO', '656': 'Springfield, MO', '657': 'Springfield, MO',
  '658': 'Springfield, MO',
  '660': 'Kansas City, KS', '661': 'Kansas City, KS', '662': 'Kansas City, KS',
  '664': 'Topeka, KS', '665': 'Topeka, KS', '666': 'Topeka, KS',
  '667': 'Fort Scott, KS', '668': 'Emporia, KS', '669': 'Hays, KS',
  '670': 'Wichita, KS', '671': 'Wichita, KS', '672': 'Wichita, KS',
  '673': 'Independence, KS', '674': 'Salina, KS', '675': 'Hutchinson, KS',
  '676': 'Hays, KS', '677': 'Colby, KS', '678': 'Dodge City, KS',
  '679': 'Liberal, KS',
  '680': 'Omaha, NE', '681': 'Omaha, NE', '683': 'Lincoln, NE',
  '684': 'Lincoln, NE', '685': 'Lincoln, NE', '686': 'Columbus, NE',
  '687': 'Norfolk, NE', '688': 'Grand Island, NE', '689': 'Hastings, NE',
  '690': 'McCook, NE', '691': 'North Platte, NE', '692': 'Valentine, NE',
  '693': 'Alliance, NE',

  // Louisiana / Arkansas
  '700': 'New Orleans, LA', '701': 'New Orleans, LA', '703': 'Thibodaux, LA',
  '704': 'Hammond, LA', '705': 'Lafayette, LA', '706': 'Lake Charles, LA',
  '707': 'Baton Rouge, LA', '708': 'Baton Rouge, LA', '710': 'Shreveport, LA',
  '711': 'Shreveport, LA', '712': 'Monroe, LA', '713': 'Alexandria, LA',
  '714': 'Alexandria, LA',
  '716': 'Pine Bluff, AR', '717': 'Camden, AR', '718': 'Texarkana, AR',
  '719': 'Hot Springs, AR', '720': 'Little Rock, AR', '721': 'Little Rock, AR',
  '722': 'Little Rock, AR', '723': 'West Memphis, AR', '724': 'Jonesboro, AR',
  '725': 'Batesville, AR', '726': 'Harrison, AR', '727': 'Fayetteville, AR',
  '728': 'Russellville, AR', '729': 'Fort Smith, AR',

  // Oklahoma
  '730': 'Oklahoma City, OK', '731': 'Oklahoma City, OK', '734': 'Ardmore, OK',
  '735': 'Lawton, OK', '736': 'Clinton, OK', '737': 'Enid, OK',
  '738': 'Woodward, OK', '739': 'Guymon, OK', '740': 'Tulsa, OK',
  '741': 'Tulsa, OK', '743': 'Miami, OK', '744': 'Muskogee, OK',
  '745': 'McAlester, OK', '746': 'Ponca City, OK', '747': 'Durant, OK',
  '748': 'Shawnee, OK', '749': 'Poteau, OK',

  // Texas
  '750': 'Dallas, TX', '751': 'Dallas, TX', '752': 'Dallas, TX',
  '753': 'Dallas, TX', '754': 'Greenville, TX', '755': 'Texarkana, TX',
  '756': 'Longview, TX', '757': 'Tyler, TX', '758': 'Palestine, TX',
  '759': 'Lufkin, TX', '760': 'Fort Worth, TX', '761': 'Fort Worth, TX',
  '762': 'Denton, TX', '763': 'Wichita Falls, TX', '764': 'Fort Worth, TX',
  '765': 'Waco, TX', '766': 'Waco, TX', '767': 'Waco, TX',
  '768': 'Abilene, TX', '769': 'Midland, TX',
  '770': 'Houston, TX', '771': 'Houston, TX', '772': 'Houston, TX',
  '773': 'Houston, TX', '774': 'Houston, TX', '775': 'Houston, TX',
  '776': 'Beaumont, TX', '777': 'Beaumont, TX', '778': 'Bryan, TX',
  '779': 'Victoria, TX', '780': 'San Antonio, TX', '781': 'San Antonio, TX',
  '782': 'San Antonio, TX', '783': 'Corpus Christi, TX', '784': 'Corpus Christi, TX',
  '785': 'McAllen, TX', '786': 'Austin, TX', '787': 'Austin, TX',
  '788': 'Del Rio, TX', '789': 'Austin, TX', '790': 'Amarillo, TX',
  '791': 'Amarillo, TX', '792': 'Childress, TX', '793': 'Lubbock, TX',
  '794': 'Lubbock, TX', '795': 'Abilene, TX', '796': 'Abilene, TX',
  '797': 'Midland, TX', '798': 'El Paso, TX', '799': 'El Paso, TX',

  // Colorado / New Mexico
  '800': 'Denver, CO', '801': 'Denver, CO', '802': 'Denver, CO',
  '803': 'Boulder, CO', '804': 'Golden, CO', '805': 'Longmont, CO',
  '806': 'Denver, CO', '807': 'Denver, CO', '808': 'Colorado Springs, CO',
  '809': 'Colorado Springs, CO', '810': 'Pueblo, CO', '811': 'Alamosa, CO',
  '812': 'Salida, CO', '813': 'Durango, CO', '814': 'Grand Junction, CO',
  '815': 'Glenwood Springs, CO', '816': 'Greeley, CO',
  '820': 'Cheyenne, WY', '821': 'Yellowstone, WY', '822': 'Wheatland, WY',
  '823': 'Rawlins, WY', '824': 'Worland, WY', '825': 'Riverton, WY',
  '826': 'Casper, WY', '827': 'Gillette, WY', '828': 'Sheridan, WY',
  '829': 'Rock Springs, WY', '830': 'Rock Springs, WY', '831': 'Rock Springs, WY',
  '832': 'Pocatello, ID', '833': 'Twin Falls, ID', '834': 'Pocatello, ID',
  '835': 'Lewiston, ID', '836': 'Boise, ID', '837': 'Boise, ID',
  '838': 'Coeur d Alene, ID',
  '840': 'Salt Lake City, UT', '841': 'Salt Lake City, UT', '842': 'Ogden, UT',
  '843': 'Ogden, UT', '844': 'Ogden, UT', '845': 'Provo, UT',
  '846': 'Provo, UT', '847': 'Price, UT',

  // Arizona / New Mexico
  '850': 'Phoenix, AZ', '851': 'Phoenix, AZ', '852': 'Phoenix, AZ',
  '853': 'Phoenix, AZ', '855': 'Globe, AZ', '856': 'Tucson, AZ',
  '857': 'Tucson, AZ', '859': 'Show Low, AZ', '860': 'Flagstaff, AZ',
  '863': 'Prescott, AZ', '864': 'Kingman, AZ', '865': 'Gallup, NM',
  '870': 'Albuquerque, NM', '871': 'Albuquerque, NM', '873': 'Gallup, NM',
  '874': 'Farmington, NM', '875': 'Albuquerque, NM', '877': 'Las Vegas, NM',
  '878': 'Socorro, NM', '879': 'Truth or Consequences, NM',
  '880': 'Las Cruces, NM', '881': 'Clovis, NM', '882': 'Roswell, NM',
  '883': 'Carrizozo, NM', '884': 'Tucumcari, NM',

  // Nevada / California
  '889': 'Las Vegas, NV', '890': 'Las Vegas, NV', '891': 'Las Vegas, NV',
  '893': 'Ely, NV', '894': 'Reno, NV', '895': 'Reno, NV',
  '897': 'Carson City, NV', '898': 'Elko, NV',
  '900': 'Los Angeles, CA', '901': 'Los Angeles, CA', '902': 'Inglewood, CA',
  '903': 'Inglewood, CA', '904': 'Santa Monica, CA', '905': 'Torrance, CA',
  '906': 'Long Beach, CA', '907': 'Long Beach, CA', '908': 'Long Beach, CA',
  '910': 'Pasadena, CA', '911': 'Pasadena, CA', '912': 'Glendale, CA',
  '913': 'Van Nuys, CA', '914': 'Van Nuys, CA', '915': 'Burbank, CA',
  '916': 'North Hollywood, CA', '917': 'Alhambra, CA', '918': 'Alhambra, CA',
  '919': 'San Diego, CA', '920': 'San Diego, CA', '921': 'San Diego, CA',
  '922': 'Palm Springs, CA', '923': 'San Bernardino, CA', '924': 'San Bernardino, CA',
  '925': 'Riverside, CA', '926': 'Santa Ana, CA', '927': 'Santa Ana, CA',
  '928': 'Anaheim, CA', '930': 'Oxnard, CA', '931': 'Santa Barbara, CA',
  '932': 'Bakersfield, CA', '933': 'Bakersfield, CA', '934': 'San Luis Obispo, CA',
  '935': 'Mojave, CA', '936': 'Fresno, CA', '937': 'Fresno, CA',
  '938': 'Fresno, CA', '939': 'Salinas, CA', '940': 'San Francisco, CA',
  '941': 'San Francisco, CA', '942': 'Sacramento, CA', '943': 'Palo Alto, CA',
  '944': 'San Mateo, CA', '945': 'Oakland, CA', '946': 'Oakland, CA',
  '947': 'Berkeley, CA', '948': 'Richmond, CA', '949': 'San Rafael, CA',
  '950': 'San Jose, CA', '951': 'San Jose, CA', '952': 'Stockton, CA',
  '953': 'Stockton, CA', '954': 'Santa Rosa, CA', '955': 'Eureka, CA',
  '956': 'Sacramento, CA', '957': 'Sacramento, CA', '958': 'Sacramento, CA',
  '959': 'Marysville, CA', '960': 'Redding, CA', '961': 'Reno, NV',

  // Oregon / Washington
  '970': 'Portland, OR', '971': 'Portland, OR', '972': 'Portland, OR',
  '973': 'Salem, OR', '974': 'Eugene, OR', '975': 'Medford, OR',
  '976': 'Klamath Falls, OR', '977': 'Bend, OR', '978': 'Pendleton, OR',
  '979': 'Portland, OR',
  '980': 'Seattle, WA', '981': 'Seattle, WA', '982': 'Everett, WA',
  '983': 'Tacoma, WA', '984': 'Tacoma, WA', '985': 'Olympia, WA',
  '986': 'Vancouver, WA', '988': 'Wenatchee, WA', '989': 'Yakima, WA',
  '990': 'Spokane, WA', '991': 'Spokane, WA', '992': 'Spokane, WA',
  '993': 'Pasco, WA', '994': 'Clarkston, WA',

  // Alaska / Hawaii
  '995': 'Anchorage, AK', '996': 'Anchorage, AK', '997': 'Fairbanks, AK',
  '998': 'Juneau, AK', '999': 'Ketchikan, AK',
  '967': 'Honolulu, HI', '968': 'Honolulu, HI',
};

/**
 * Resolve a ZIP code to a "City, State" label. Falls back to the provided
 * state (or null) when the ZIP3 is not in the lookup.
 */
export function zipToCityState(zip: string | undefined, stateFallback?: string): string | null {
  if (!zip) return stateFallback ?? null;
  const digits = zip.replace(/\D/g, '').slice(0, 5);
  if (digits.length < 3) return stateFallback ?? null;
  return ZIP3_TO_CITY[digits.slice(0, 3)] ?? stateFallback ?? null;
}
